"use client";

import { HEADER_ICON_OPTIONS } from "@/app/api/data/HeaderIconOptions";
import { HEADER_TEXT_OPTIONS } from "@/app/api/data/HeaderTextOptions";
import { TEMPLATE_LIST } from "@/app/api/data/TemplateMetaData";
import {
  FORM_DATA_LOCAL_STORAGE_KEY,
  SETTINGS_LOCAL_STORAGE_KEY,
} from "@/constants/keys";
import { FontFamily, HeaderStyle, SupportedLanguage } from "@/enum";
import {
  HeaderIcon,
  HeaderIconMeta,
  LocalStorageFormData,
  Template,
  TemplateMetaData,
} from "@/types";

import {
  CUSTOM_ICONS_STORE_NAME,
  initDB as initCustomIconsDB,
} from "@/utils/DBUtils";

// Utility function to get data from local storage
export const getLocalStorageData = (
  key: string
): LocalStorageFormData | null => {
  const localStorageDataString = localStorage.getItem(key);
  return localStorageDataString ? JSON.parse(localStorageDataString) : null;
};

// Utility function to set data in local storage
export const setLocalStorageData = (
  key: string,
  data: LocalStorageFormData
): void => {
  localStorage.setItem(key, JSON.stringify(data));
};

// Utility function to get the selected template from local storage
export const getTemplate = (): Template => {
  const localStorageData: LocalStorageFormData | null = getLocalStorageData(
    FORM_DATA_LOCAL_STORAGE_KEY
  );
  if (localStorageData) {
    return (
      TEMPLATE_LIST.find((t) => t.id === localStorageData.selectedTemplateId) ||
      TEMPLATE_LIST[0]
    );
  }
  return TEMPLATE_LIST[0];
};

// Utility function to set the selected template ID in local storage
export const setSelectedTemplateIdInLocalStorage = (id: string): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    selectedTemplateId: id,
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};

// Utility function to get all custom header icons from IndexedDB
export const getAllCustomHeaderIcons = async (): Promise<HeaderIcon[]> => {
  try {
    const db = await initCustomIconsDB();
    const transaction = db.transaction(CUSTOM_ICONS_STORE_NAME, "readonly");
    const store = transaction.objectStore(CUSTOM_ICONS_STORE_NAME);
    const request = store.getAll();

    const result = await new Promise<any[]>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    return result || [];
  } catch (error) {
    console.error("Failed to get custom header icons", error);
    return [];
  }
};

// Utility function to save custom header icon to IndexedDB
export const saveCustomHeaderIcon = async (icon: HeaderIcon): Promise<void> => {
  try {
    const db = await initCustomIconsDB();
    const transaction = db.transaction(CUSTOM_ICONS_STORE_NAME, "readwrite");
    const store = transaction.objectStore(CUSTOM_ICONS_STORE_NAME);

    // Convert meta to blob if it's an object with a blob URL
    let dataToStore = icon;
    if (
      typeof icon.meta === "object" &&
      icon.meta &&
      "src" in icon.meta &&
      typeof icon.meta.src === "string" &&
      icon.meta.src.startsWith("blob:")
    ) {
      const response = await fetch(icon.meta.src);
      const blob = await response.blob();
      dataToStore = {
        ...icon,
        meta: { ...icon.meta, blob: blob } as any,
      };
    }

    await new Promise<void>((resolve, reject) => {
      const request = store.put(dataToStore, icon.id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Failed to save custom header icon", error);
    throw error;
  }
};

// Utility function to delete custom header icon from IndexedDB
export const deleteCustomHeaderIcon = async (iconId: string): Promise<void> => {
  try {
    const db = await initCustomIconsDB();
    const transaction = db.transaction(CUSTOM_ICONS_STORE_NAME, "readwrite");
    const store = transaction.objectStore(CUSTOM_ICONS_STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(iconId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Failed to delete custom header icon", error);
    throw error;
  }
};

// Utility function to create HeaderIconMeta from blob URL
export const createHeaderIconMetaFromBlob = async (
  blobUrl: string,
  id: string
) => {
  const img = new Image();
  img.src = blobUrl;

  return new Promise<HeaderIconMeta>((resolve) => {
    img.onload = () => {
      resolve({
        src: blobUrl,
        width: img.width,
        height: img.height,
        blurWidth: 10,
        blurHeight: 10,
      });
    };
  });
};

// Utility function to get the selected header icon from local storage
// Note: This only checks built-in icons. Custom icons are handled in the component
export const getSelectedHeaderIcon = async (): Promise<HeaderIcon | null> => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  if (localStorageData?.selectedHeaderIconId) {
    // Check if it's a custom icon first
    if (localStorageData.selectedHeaderIconId.startsWith("custom_icon_")) {
      try {
        const customIcons = await getAllCustomHeaderIcons();
        const customIcon = customIcons.find(
          (icon) => icon.id === localStorageData.selectedHeaderIconId
        );
        if (
          customIcon &&
          typeof customIcon.meta === "object" &&
          customIcon.meta &&
          "blob" in customIcon.meta &&
          customIcon.meta.blob
        ) {
          // Convert blob back to object URL
          const objectUrl = URL.createObjectURL(customIcon.meta.blob);
          return {
            ...customIcon,
            meta: {
              ...customIcon.meta,
              src: objectUrl,
              blurWidth: 10,
              blurHeight: 10,
            },
          };
        }
        if (customIcon) {
          return customIcon;
        }
      } catch (error) {
        console.error("Failed to load custom icon", error);
      }
    }
    // Check built-in icons
    const builtInIcon = HEADER_ICON_OPTIONS.find(
      (icon) => icon.id === localStorageData.selectedHeaderIconId
    );
    if (builtInIcon) {
      return builtInIcon;
    }
    return HEADER_ICON_OPTIONS[0];
  }
  const selectedReligion = JSON.parse(
    localStorage.getItem(SETTINGS_LOCAL_STORAGE_KEY) || "{}"
  ).religion;
  return (
    HEADER_ICON_OPTIONS.find(
      (option) => option.religion === selectedReligion
    ) || HEADER_ICON_OPTIONS[0]
  );
};

// Utility function to set the selected header icon in local storage
export const setSelectedHeaderIconInLocalStorage = (id: string): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    selectedHeaderIconId: id,
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};

// Utility function to get the selected header text from local storage
export const getSelectedHeaderText = (
  locale: SupportedLanguage
): string | null => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  const settings = localStorage.getItem(SETTINGS_LOCAL_STORAGE_KEY);
  const selectedReligion = settings ? JSON.parse(settings).religion : null;

  // Filter HEADER_TEXT_OPTIONS based on the provided locale
  const filteredOptions = HEADER_TEXT_OPTIONS.filter((option) =>
    option.locale.includes(locale)
  );

  // If localStorageData exists, return the selectedHeaderText
  if (localStorageData && localStorageData.selectedHeaderText) {
    return localStorageData.selectedHeaderText;
  }

  const sortedOptions = filteredOptions.sort((a, b) => {
    // First sort by religion match
    if (b.religion === selectedReligion && a.religion !== selectedReligion)
      return 1;
    if (a.religion === selectedReligion && b.religion !== selectedReligion)
      return -1;
    // Secondary sort by id to ensure consistent ordering
    return a.id.localeCompare(b.id);
  });

  // If no localStorageData, return the first matching option or default to the first option
  return sortedOptions.length > 0
    ? sortedOptions[0].text
    : HEADER_TEXT_OPTIONS[0].text;
};

// Utility function to set the selected header text in local storage
export const setSelectedHeaderTextInLocalStorage = (text: string): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    selectedHeaderText: text,
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};

// Utility function to get the header style (mutually-exclusive layout) from local storage.
// Falls back to the historical behaviour for users saved before this field existed.
export const getHeaderStyle = (): HeaderStyle => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  if (localStorageData?.selectedHeaderStyle) {
    return localStorageData.selectedHeaderStyle;
  }
  // Backward-compat: infer from any previously stored values.
  if (localStorageData?.selectedBiodataTitle) {
    return HeaderStyle.BIODATA_TITLE;
  }
  // Header icon/text has always been the default layout.
  return HeaderStyle.ICON_TEXT;
};

// Utility function to set the header style in local storage
export const setHeaderStyleInLocalStorage = (style: HeaderStyle): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    selectedHeaderStyle: style,
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};

// Utility function to get the biodata title from local storage.
// Returns null when the user has not opted in (the feature is off by default so
// it never double-renders on templates that bake the title into the background).
export const getBiodataTitle = (): string | null => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  return localStorageData?.selectedBiodataTitle ?? null;
};

// Utility function to set the biodata title in local storage (null = hidden)
export const setBiodataTitleInLocalStorage = (
  title: string | null
): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    selectedBiodataTitle: title,
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};

// Default font values
export const DEFAULT_TITLE_FONT_SIZE = 14;
export const DEFAULT_TEXT_FONT_SIZE = 12;
export const DEFAULT_PROFILE_PICTURE_SCALE = 1.0;

// Utility function to get font settings from local storage
export const getFontSettings = (): {
  titleFontSize: number;
  textFontSize: number;
} => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  return {
    titleFontSize: localStorageData?.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE,
    textFontSize: localStorageData?.textFontSize ?? DEFAULT_TEXT_FONT_SIZE,
  };
};

// Utility function to set font settings in local storage
export const setFontSettings = (
  titleFontSize: number,
  textFontSize: number
): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    titleFontSize,
    textFontSize,
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};

// About Me / Expectations free-text blocks (used by layouts such as ARCH).
export const getAboutExpectations = (): { aboutMe: string; expectations: string } => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  return {
    aboutMe: localStorageData?.aboutMe ?? "",
    expectations: localStorageData?.expectations ?? "",
  };
};

export const setAboutExpectations = (
  aboutMe: string,
  expectations: string
): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, {
    ...localStorageData,
    aboutMe,
    expectations,
  });
};

// Per-template font sizes: each template seeds from its metadata default and the user
// can override via the slider. Overrides are stored per template id so one template's
// size never leaks into another.
export const getFontSettingsForTemplate = (
  templateId: string,
  metadata: TemplateMetaData
): { titleFontSize: number; textFontSize: number } => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  const stored = localStorageData?.fontSizesByTemplate?.[templateId];
  return {
    titleFontSize:
      stored?.titleFontSize ?? metadata.TITLE_FONT_SIZE ?? DEFAULT_TITLE_FONT_SIZE,
    textFontSize:
      stored?.textFontSize ?? metadata.TEXT_FONT_SIZE ?? DEFAULT_TEXT_FONT_SIZE,
  };
};

export const setFontSettingsForTemplate = (
  templateId: string,
  titleFontSize: number,
  textFontSize: number
): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    fontSizesByTemplate: {
      ...(localStorageData.fontSizesByTemplate || {}),
      [templateId]: { titleFontSize, textFontSize },
    },
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};

// Default English font family used when a template carries no FONT_FAMILY default.
export const DEFAULT_FONT_FAMILY = FontFamily.POPPINS;

// Per-template English font family: seeds from the template's metadata default,
// the user can override, and the choice is stored per template id. Non-English
// locales ignore this and always use their script-specific fonts.
export const getFontFamilyForTemplate = (
  templateId: string,
  metadata: TemplateMetaData
): FontFamily => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  return (
    localStorageData?.fontFamilyByTemplate?.[templateId] ??
    metadata.FONT_FAMILY ??
    DEFAULT_FONT_FAMILY
  );
};

export const setFontFamilyForTemplate = (
  templateId: string,
  fontFamily: FontFamily
): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    fontFamilyByTemplate: {
      ...(localStorageData.fontFamilyByTemplate || {}),
      [templateId]: fontFamily,
    },
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};

// Utility function to get profile picture scale from local storage
export const getProfilePictureScale = (): number => {
  const localStorageData = getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY);
  return localStorageData?.profilePictureScale ?? DEFAULT_PROFILE_PICTURE_SCALE;
};

// Utility function to set profile picture scale in local storage
export const setProfilePictureScale = (scale: number): void => {
  const localStorageData =
    getLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY) ||
    ({} as LocalStorageFormData);
  const updatedData: LocalStorageFormData = {
    ...localStorageData,
    profilePictureScale: scale,
  };
  setLocalStorageData(FORM_DATA_LOCAL_STORAGE_KEY, updatedData);
};
